import React from "react";
import {
  Box,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography
} from "@material-ui/core";
import { HAKLAI_ADD_MODES } from "../../shared/haklaiConstants";
import {
  HaklaiStyledHeaderCell as StyledHeaderCell,
  HaklaiStyledNameHeaderCell as StyledNameHeaderCell,
  haklaiNameColumnWidth
} from "../../shared/table/haklaiTableShared";
import HaklaiAddPillButton from "../../shared/ui/HaklaiAddPillButton";
import {
  ACTION_COLUMN_WIDTH,
  DEPRECIATION_COLUMN_WIDTH,
  PROPERTY_TYPE_COLUMN_WIDTH,
  READ_ONLY_VALUE_COLUMN_WIDTH,
  REMAINING_COLUMN_WIDTH,
  STANDARD_VALUE_COLUMN_WIDTH,
  StyledGroupCell
} from "../haklaiPropertyPartsStyles";
import {
  HAKLAI_PROPERTY_PART_LABELS,
  HAKLAI_PROPERTY_PART_TOOLTIPS
} from "../haklaiPropertyPartsTexts";

const sectionAddMenuProps = {
  getContentAnchorEl: null,
  anchorOrigin: { vertical: "bottom", horizontal: "right" },
  transformOrigin: { vertical: "top", horizontal: "right" }
};
const FLOATING_TOOLBAR_SAFE_GUTTER = 48;
const PROPERTY_PARTS_TABLE_BASE_MIN_WIDTH = PROPERTY_TYPE_COLUMN_WIDTH
  + haklaiNameColumnWidth
  + STANDARD_VALUE_COLUMN_WIDTH
  + REMAINING_COLUMN_WIDTH
  + ACTION_COLUMN_WIDTH;

const getZabanColumnWidth = (hasPre1997ConstructionQuestion) => (
  hasPre1997ConstructionQuestion ? STANDARD_VALUE_COLUMN_WIDTH : READ_ONLY_VALUE_COLUMN_WIDTH
);

const getPropertyPartsTableMinWidth = ({
  showBuiltValueColumn,
  showZabanColumn,
  hasPre1997ConstructionQuestion
}) => (
  PROPERTY_PARTS_TABLE_BASE_MIN_WIDTH
  + (showBuiltValueColumn ? STANDARD_VALUE_COLUMN_WIDTH : 0)
  + (showZabanColumn ? getZabanColumnWidth(hasPre1997ConstructionQuestion) : 0)
  + DEPRECIATION_COLUMN_WIDTH
);

const getPropertyPartsColumnCount = ({ showBuiltValueColumn, showZabanColumn }) => (
  5
  + (showBuiltValueColumn ? 1 : 0)
  + (showZabanColumn ? 1 : 0)
  + 1
);

const HaklaiSectionAddMenu = ({
  classes,
  anchorEl,
  onClose,
  highlightedCategoryId,
  items
}) => (
  <Menu
    anchorEl={anchorEl}
    open={Boolean(anchorEl)}
    onClose={onClose}
    {...sectionAddMenuProps}
  >
    {items.map((item) => {
      const isDisabled = item.addState.mode === HAKLAI_ADD_MODES.UNAVAILABLE;

      return (
        <MenuItem
          key={item.categoryId}
          onMouseDown={(event) => {
            event.preventDefault();
          }}
          onClick={item.onSelect}
          disabled={isDisabled}
          selected={highlightedCategoryId === item.categoryId && !isDisabled}
          className={classes.sectionAddMenuItem}
        >
          <Box className={classes.sectionAddMenuItemContent}>
            <Typography className={classes.sectionAddMenuItemTitle}>
              {item.label}
            </Typography>
          </Box>
        </MenuItem>
      );
    })}
  </Menu>
);

const HaklaiPropertySection = React.memo(function HaklaiPropertySection({
  classes,
  sectionId,
  sectionTitle,
  omitTopFrameOnFirstBody = false,
  addMode,
  addAriaLabel,
  onAddClick,
  bodies,
  renderRow,
  showBuiltValueColumn,
  showZabanColumn
}) {
  const optionalResidentialColumnCount =
    (showBuiltValueColumn ? 1 : 0)
    + (showZabanColumn ? 1 : 0);
  const columnCount = getPropertyPartsColumnCount({ showBuiltValueColumn, showZabanColumn });

  return (
    <>
      {bodies.map((body, bodyIndex) => (
        <TableBody
          key={`${sectionId}_${body.bodyKey}`}
          className={[
            classes.sectionFrameBody,
            bodyIndex > 0 || omitTopFrameOnFirstBody ? classes.sectionFrameBodyNoTop : ""
          ].filter(Boolean).join(" ")}
        >
          {bodyIndex === 0 ? (
            <TableRow>
              <StyledGroupCell align="center" className={classes.groupHeadLeadCell}>
                <Typography
                  variant="body2"
                  component="div"
                  dir="rtl"
                  className={classes.groupHeaderLeadTitle}
                >
                  {sectionTitle}
                </Typography>
              </StyledGroupCell>

              <StyledGroupCell align="right" className={classes.groupHeaderSpacerCell}>
                <Box className={classes.groupHeaderSpacer} />
              </StyledGroupCell>

              <StyledGroupCell align="right" className={classes.groupHeaderSpacerCell}>
                <Box className={classes.groupHeaderSpacer} />
              </StyledGroupCell>

              {optionalResidentialColumnCount > 0 ? (
                <StyledGroupCell
                  colSpan={optionalResidentialColumnCount}
                  align="right"
                  className={classes.groupHeaderSpacerCell}
                >
                  <Box className={classes.groupHeaderSpacer} />
                </StyledGroupCell>
              ) : null}

              <StyledGroupCell
                colSpan={2}
                align="right"
                className={classes.groupHeaderSpacerCell}
              >
                <Box className={classes.groupHeaderSpacer} />
              </StyledGroupCell>

              <StyledGroupCell align="center" className={classes.groupHeadActionCell}>
                <Box className={classes.groupHeaderActionWrap}>
                  <HaklaiAddPillButton
                    disabled={addMode === HAKLAI_ADD_MODES.UNAVAILABLE}
                    mode={addMode}
                    onClick={onAddClick}
                    classes={classes}
                    label={addAriaLabel}
                  />
                </Box>
              </StyledGroupCell>
            </TableRow>
          ) : null}

          {(body.rows || []).length === 0 ? (
            <TableRow>
              <StyledGroupCell colSpan={columnCount} className={classes.emptyStateCell}>
                <Typography component="span" className={classes.emptyStateText}>
                  {HAKLAI_PROPERTY_PART_LABELS.emptySection}
                </Typography>
              </StyledGroupCell>
            </TableRow>
          ) : (body.rows || []).map(([rowKey, part], rowIndex) => renderRow(rowKey, part, {
              subSectionLabel: body.subSectionLabel,
              subSectionRowSpan: body.rows.length,
              isFirstInSubSection: rowIndex === 0,
              isFirstInBody: bodyIndex === 0 && rowIndex === 0,
              isLastInBody: false
            }))}
        </TableBody>
      ))}
    </>
  );
});

const HaklaiPropertyPartsTable = ({
  classes,
  stickyHeaderEnabled,
  showBuiltValueColumn,
  showZabanColumn,
  hasPre1997ConstructionQuestion,
  sections,
  menus
}) => {
  const zabanColumnWidth = getZabanColumnWidth(hasPre1997ConstructionQuestion);
  const tableWidth = getPropertyPartsTableMinWidth({
    showBuiltValueColumn,
    showZabanColumn,
    hasPre1997ConstructionQuestion
  });
  const tableFrameRef = React.useRef(null);
  const [isTableScrollContained, setIsTableScrollContained] = React.useState(false);
  React.useEffect(() => {
    const node = tableFrameRef.current;
    if (!node) return undefined;

    const updateScrollContainment = () => {
      const availableWidth = node.clientWidth || 0;
      const shouldContain = availableWidth > 0 && availableWidth < tableWidth + FLOATING_TOOLBAR_SAFE_GUTTER;
      setIsTableScrollContained((prev) => (prev === shouldContain ? prev : shouldContain));
    };

    updateScrollContainment();
    const ResizeObserverImpl = window.ResizeObserver;
    const resizeObserver = ResizeObserverImpl ? new ResizeObserverImpl(updateScrollContainment) : null;
    if (resizeObserver) resizeObserver.observe(node);
    window.addEventListener("resize", updateScrollContainment);

    return () => {
      if (resizeObserver) resizeObserver.disconnect();
      window.removeEventListener("resize", updateScrollContainment);
    };
  }, [tableWidth]);

  // Menus live as siblings of the TableContainer so the <table> subtree
  // stays structurally clean (no popovers interleaved with rows). MUI
  // portals the menu to document.body anyway, so positioning is unaffected.
  return (
    <>
      <Box ref={tableFrameRef} className={classes.tableFrameAlign}>
        <TableContainer
          className={[
            classes.tableWrapper,
            isTableScrollContained ? classes.tableWrapperScrollable : ""
          ].filter(Boolean).join(" ")}
        >
          <Table
            size="small"
            style={{
              tableLayout: "fixed",
              width: "100%",
              minWidth: tableWidth,
              borderCollapse: "collapse",
              backgroundColor: "#fff"
            }}
          >
        <TableHead
          className={[
            classes.stickyHeader,
            (!stickyHeaderEnabled || isTableScrollContained) ? classes.stickyHeaderDisabled : "",
          ]
            .filter(Boolean)
            .join(" ")}
        >
          <TableRow>
            <StyledHeaderCell
              className={classes.propertyTableHeaderCell}
              style={{ width: PROPERTY_TYPE_COLUMN_WIDTH }}
            >
              <Typography component="span" className={classes.headerLabelText}>
                {HAKLAI_PROPERTY_PART_LABELS.assetType}
              </Typography>
            </StyledHeaderCell>

            <StyledNameHeaderCell className={classes.propertyTableHeaderCell}>
              <Typography component="span" className={classes.headerLabelText}>
                {HAKLAI_PROPERTY_PART_LABELS.farmAssets}
              </Typography>
            </StyledNameHeaderCell>

            <StyledHeaderCell
              className={classes.propertyTableHeaderCell}
              style={{ width: STANDARD_VALUE_COLUMN_WIDTH }}
            >
              <Typography component="span" className={classes.headerLabelText}>
                {HAKLAI_PROPERTY_PART_LABELS.relativeSaleValue}
              </Typography>
            </StyledHeaderCell>

            <StyledHeaderCell
              className={classes.propertyTableHeaderCell}
              style={{ width: REMAINING_COLUMN_WIDTH }}
            >
              <Tooltip title={HAKLAI_PROPERTY_PART_TOOLTIPS.autoCalculated}>
                <Typography component="span" className={classes.headerLabelText}>
                  {HAKLAI_PROPERTY_PART_LABELS.remainingToAllocate}
                </Typography>
              </Tooltip>
            </StyledHeaderCell>

            {showBuiltValueColumn ? (
              <StyledHeaderCell
                className={[
                  classes.propertyTableHeaderCell,
                  classes.propertyTableTrailingHeaderCell,
                  classes.propertyTableGroupBoundaryHeaderCell,
                  showZabanColumn ? "" : classes.propertyTableGroupEndHeaderCell
                ].join(" ")}
                style={{ width: STANDARD_VALUE_COLUMN_WIDTH }}
              >
                <Typography component="span" className={classes.headerLabelText}>
                  {HAKLAI_PROPERTY_PART_LABELS.builtValue}
                </Typography>
              </StyledHeaderCell>
            ) : null}

            {showZabanColumn ? (
              <StyledHeaderCell
                className={[
                  classes.propertyTableHeaderCell,
                  classes.propertyTableTrailingHeaderCell,
                  !showBuiltValueColumn ? classes.propertyTableGroupBoundaryHeaderCell : "",
                  classes.propertyTableGroupEndHeaderCell
                ].join(" ")}
                style={{ width: zabanColumnWidth }}
              >
                <Tooltip title={HAKLAI_PROPERTY_PART_TOOLTIPS.autoCalculatedWhenZabanSelected}>
                  <Typography component="span" className={classes.headerLabelText}>
                    {HAKLAI_PROPERTY_PART_LABELS.zabanValue}
                  </Typography>
                </Tooltip>
              </StyledHeaderCell>
            ) : null}

            <StyledHeaderCell
              className={[
                classes.propertyTableHeaderCell,
                classes.propertyTableTrailingHeaderCell,
              ].join(" ")}
              style={{ width: DEPRECIATION_COLUMN_WIDTH }}
              aria-label={HAKLAI_PROPERTY_PART_LABELS.rentalDepreciation}
            >
              <Tooltip title={HAKLAI_PROPERTY_PART_TOOLTIPS.rentalDepreciationForPart}>
                <Typography component="span" className={classes.headerLabelText}>
                  {HAKLAI_PROPERTY_PART_LABELS.rentalDepreciation}
                </Typography>
              </Tooltip>
            </StyledHeaderCell>

            <StyledHeaderCell
              className={[
                classes.propertyTableHeaderCell,
                classes.propertyTableTrailingHeaderCell,
              ].join(" ")}
              style={{ width: ACTION_COLUMN_WIDTH, minWidth: ACTION_COLUMN_WIDTH }}
            />
          </TableRow>
        </TableHead>

        {sections.map((section, sectionIndex) => (
          <HaklaiPropertySection
            key={section.sectionId}
            classes={classes}
            sectionId={section.sectionId}
            sectionTitle={section.sectionTitle}
            omitTopFrameOnFirstBody={sectionIndex > 0}
            addMode={section.addMode}
            addAriaLabel={section.addAriaLabel}
            onAddClick={section.onAddClick}
            bodies={section.bodies}
            renderRow={section.renderRow}
            showBuiltValueColumn={showBuiltValueColumn}
            showZabanColumn={showZabanColumn}
          />
        ))}
          </Table>
        </TableContainer>
      </Box>

      {menus.map((menu) => (
        <HaklaiSectionAddMenu
          key={menu.menuKey}
          classes={classes}
          anchorEl={menu.anchorEl}
          onClose={menu.onClose}
          highlightedCategoryId={menu.highlightedCategoryId}
          items={menu.items}
        />
      ))}
    </>
  );
};

export default React.memo(HaklaiPropertyPartsTable);
